import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, Award, Shield, CheckCircle, Users, X } from 'lucide-react';

const WebinarRegistration: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setShowPopup(false);
  };

  // Function to handle closing the popup
  const handleClosePopup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopup(false);
  };

  const webinarDetails = {
    date: 'March 28, 2025',
    time: '2:00 PM EST',
    duration: '60 minutes',
    speaker: {
      name: 'Michael Anderson, CFP®',
      title: 'Senior Financial Advisor',
      experience: '25+ years of experience',
      credentials: ['Certified Financial Planner™', 'Chartered Financial Consultant®']
    },
    seatsRemaining: 47
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <div className="inline-flex items-center bg-primary/10 rounded-full px-4 py-2 text-primary mb-6">
                <Calendar className="h-5 w-5 mr-2" />
                Live Webinar: {webinarDetails.date}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                Discover How to Create a
                <span className="block text-primary">Tax-Free Retirement</span>
              </h1>
              <div className="mt-6 space-y-4 text-gray-600">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-3 text-primary" />
                  <span>{webinarDetails.time} ({webinarDetails.duration})</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-3 text-primary" />
                  <span>Only {webinarDetails.seatsRemaining} seats remaining</span>
                </div>
              </div>
              <div className="mt-8">
                <button
                  onClick={() => setShowPopup(true)}
                  className="btn btn-primary px-8 py-3 text-lg"
                >
                  Reserve Your Seat Now
                </button>
              </div>
            </div>
            
            {/* Speaker Profile */}
            <div className="mt-12 lg:mt-0">
              <div className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-10 w-10 text-primary" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">{webinarDetails.speaker.name}</h3>
                    <p className="text-gray-600">{webinarDetails.speaker.title}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  With {webinarDetails.speaker.experience} in retirement planning and tax strategies.
                </p>
                <div className="space-y-3">
                  {webinarDetails.speaker.credentials.map((credential, index) => (
                    <div key={index} className="flex items-center">
                      <Award className="h-5 w-5 text-primary mr-2" />
                      <span>{credential}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What You'll Learn Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Tax-Free Income Strategies",
                description: "Learn how to structure your retirement accounts to minimize tax liability."
              },
              {
                title: "Roth Conversion Tactics",
                description: "Discover the best times and methods for converting traditional IRAs to Roth IRAs."
              },
              {
                title: "Social Security Optimization",
                description: "Maximize your Social Security benefits while reducing taxes on your benefits."
              }
            ].map((topic, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <CheckCircle className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">{topic.title}</h3>
                <p className="text-gray-600">{topic.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Popup Registration Form */}
      {showPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-lg p-8 max-w-md w-full mx-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <h2 className="text-2xl font-bold">Reserve Your Seat</h2>
              </div>
              <div className="flex items-center">
                <div className="text-sm text-primary font-medium mr-4">
                  {webinarDetails.seatsRemaining} seats left
                </div>
                <button 
                  onClick={handleClosePopup}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-start mt-6">
                <Shield className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-500">
                  Your information is secure and will never be shared with third parties.
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleClosePopup}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Reserve My Seat
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebinarRegistration;